import { css } from '@emotion/core'
import * as React from 'react'

import { emptySpaceWidthBreakPoint, headerBreakPoint } from '../constants/breakPoints'
import { breakpoints } from '../constants/breakPoints'
import { headerHeight } from '../constants/header'
import { isDesktop, isMobile } from '../helpers/common'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import { useDetectSticky } from '../hooks/useStickyObserver'
// import ScrollToTop from './ScrollToTop'
import LoupeIcon from '../icons/abstract/loupe.svg'
import { NoSearchResultBlockProps, SearchBarProps, SearchResultsBlockProps } from '../interfaces/search'
import Block from './Block'
import CTAMain from './ctas/CTAMain'
import CTANewsletter from './ctas/CTANewsletter'
import Image from './Image'
import { LoadingSpinner } from './LoadingComponent'

export const searchBarHeight = 83 // px

const scrollSearchBarToTop = () =>
  window.scrollTo({
    top: document.getElementById('search-bar')!.getBoundingClientRect().top + window.scrollY - headerHeight,
    behavior: 'smooth',
  })

const searchBarInputRef = React.createRef<HTMLInputElement>()
const searchLoupeRef = React.createRef<HTMLDivElement>()

const focusSearchBarInput = (preventScrollForLoupeAndSlidingComponent?: boolean) => {
  searchBarInputRef.current?.focus({
    preventScroll: isMobile(breakpoints.small) || preventScrollForLoupeAndSlidingComponent,
  })
}

const animateSearchLoupe = (visible: boolean, isSlidingComponent?: boolean) => {
  const searchLoupe = searchLoupeRef.current
  if (searchLoupe) {
    searchLoupe.style.opacity = visible ? '1' : '0'
    searchLoupe.style.transform = visible
      ? 'translate(0)'
      : `${isSlidingComponent ? 'translateX(-80px)' : 'translateY(-50px)'}`
  }
}

export const SearchBarComponent: React.FunctionComponent<SearchBarProps> = ({
  id,
  search,
  setSearch,
  blackBG = false,
  placeholder = 'Search...',
  isPartOfHeader,
  isInSidebar = false,
  isInSearchResultsBlock = false,
  isSlidingComponent = false,
  hideOnScrollAndScrollingDown = false,
  breakPoint = headerBreakPoint,
  cssStyles,
}) => {
  const debouncedSearch = useDebouncedValue(search)

  const [searchBarIsFocused, setSearchBarIsFocused] = React.useState(false)

  // to add bottom-border & background styles when search bar becomes sticky
  const searchBarIsStuck =
    isMobile(breakpoints.small) || !(isPartOfHeader || isInSidebar)
      ? useDetectSticky('search-bar', headerHeight)
      : false

  React.useEffect(() => {
    // after LoadingSpinner (debouncedSearch updated) - scroll SearchResultsBlock to top
    document.getElementById('search-results-block')?.scrollTo({ top: 0, behavior: 'smooth' })

    // focus SearchBar of SearchResultsBlock once sliding up
    if (isInSidebar) {
      document.getElementById('search-results-block')?.querySelector('input')?.focus()
    }
  }, [debouncedSearch])

  React.useEffect(() => {
    if (hideOnScrollAndScrollingDown) {
      searchBarInputRef.current?.blur()
    }
  }, [hideOnScrollAndScrollingDown])

  React.useEffect(
    () => {
      // (background) body not scrollable if a search term is entered
      document.body.style.overflow = search ? 'hidden' : 'auto'
      // if typing and search bar is not stuck: scroll search bar to the top of the screenview
      if (!isPartOfHeader && !isInSidebar && !isInSearchResultsBlock && !!searchBarIsFocused) {
        scrollSearchBarToTop()
      }
    },
    // fires only at first amendment of search term
    [search === debouncedSearch]
  )

  // for tablet & mobile:
  // when search bar is focused: always scroll the search bar to the top of the screenview
  // below the header to be not overlayed by the touch keyboard sliding in from below
  React.useEffect(() => {
    if (!isPartOfHeader && !!searchBarIsFocused && !searchBarIsStuck && !isSlidingComponent && isMobile(breakPoint)) {
      scrollSearchBarToTop()
    }
  }, [searchBarIsFocused])

  return (
    <div
      id={isPartOfHeader ? undefined : !!id ? id : 'search-bar'}
      className={!(isPartOfHeader || isInSidebar) ? 'full-screen-sticky' : ''}
      css={[
        searchBarContainerStyle(
          blackBG,
          searchBarIsFocused,
          isInSidebar,
          hideOnScrollAndScrollingDown,
          isSlidingComponent,
          searchBarIsStuck,
          isPartOfHeader
        ),
        cssStyles,
      ]}
    >
      <Block
        columns={3}
        columnsTemplate="20px auto 20px"
        noBreak
        css={searchBarBlockStyle(breakPoint, searchBarIsFocused, blackBG, isPartOfHeader)}
      >
        <LoupeIcon />
        <input
          ref={isInSearchResultsBlock ? null : searchBarInputRef}
          className={isPartOfHeader ? 'body-2 no-margin' : 'body-1 no-margin'}
          placeholder={placeholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => {
            setSearchBarIsFocused(true)
            animateSearchLoupe(false, isSlidingComponent)
          }}
          onBlur={() => {
            if (!(isMobile(headerBreakPoint) || isSlidingComponent)) {
              setSearchBarIsFocused(false)
            }

            // tablet & mobile: keep search bar visible when search term is entered OR
            // if isSlidingComponent (e.g. templates pages)
            if ((isMobile(headerBreakPoint) || isSlidingComponent) && !search) {
              setSearchBarIsFocused(false)
              animateSearchLoupe(true)
            }
          }}
        />
        {search && search !== debouncedSearch ? (
          <LoadingSpinner blackBG={blackBG} small />
        ) : (
          <div
            onClick={() => (
              setSearch(''),
              isMobile(breakpoints.small) || isSlidingComponent
                ? (setSearchBarIsFocused(false), animateSearchLoupe(true))
                : focusSearchBarInput(isSlidingComponent)
            )}
            css={searchDeleteImageStyle(!!search)}
          >
            <Image
              src="icons/x.webp"
              alt="airfocus search exit"
              imgStyle={{ width: '10px', height: '10px', margin: 'auto' }}
            />
          </div>
        )}
      </Block>
    </div>
  )
}

// Search Bar
const SearchBar: React.FunctionComponent<SearchBarProps> = (props) => {
  const { useLoupeIconOnly, isPartOfHeader, isSlidingComponent, cssStyles } = props

  if (useLoupeIconOnly || (isPartOfHeader && isMobile(breakpoints.small))) {
    return (
      <div
        ref={searchLoupeRef}
        onClick={() => focusSearchBarInput(useLoupeIconOnly)}
        css={[loupMobileStyle, !!cssStyles && cssStyles]}
      >
        <LoupeIcon />
      </div>
    )
  }

  if (isMobile(breakpoints.small) || isSlidingComponent) {
    return (
      <div css={mobileSearchBarWrapperStyle}>
        <SearchBarComponent {...props} />
      </div>
    )
  }

  return <SearchBarComponent {...props} />
}

export default SearchBar

const loupMobileStyle = css`
  cursor: pointer;
  display: flex;
  width: 20px;
  transition: all 0.3s ease-in-out;
`

const mobileSearchBarWrapperStyle = css`
  position: sticky;
  top: ${headerHeight}px;
  max-height: 0;
  overflow: visible;
  // z-index: 6 (z < 7 && z > 5) is important!
  // Otherwise the SearchBar is NOT overlayed by Header (z-index: 7)
  // or IS overlayed by the SearchResultsBlock (z-index: 5)
  // or IS overlayed by the LoadingSpinner (z-index: 4)
  // or IS Overlayed by the Anchors & ScrollToTop (z-index: 3)
  z-index: 6;
`

const searchBarContainerStyle = (
  blackBG: boolean,
  searchBarIsFocused: boolean,
  isInSidebar: boolean,
  hideOnScrollAndScrollingDown: boolean,
  isSlidingComponent: boolean,
  searchBarIsStuck?: boolean,
  isPartOfHeader?: boolean
) => css`
  // z-index: 6 (z < 7 && z > 5) is important!
  // Otherwise the SearchBar is NOT overlayed by Header (z-index: 7)
  // or IS overlayed by the SearchResultsBlock (z-index: 5)
  // or IS overlayed by the LoadingSpinner (z-index: 4)
  // or IS Overlayed by the Anchors & ScrollToTop (z-index: 3)
  ${!isInSidebar && 'z-index: 6'};

  ${!isPartOfHeader &&
  (isInSidebar
    ? `
        margin: 0 0 1.5rem !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
        position: relative;
        top: 0;
      `
    : `
        background: ${blackBG ? 'rgba(7, 19, 34, 0.8)' : 'rgba(255, 255, 255, 0.8)'} ;
        backdrop-filter: blur(20px);
        position: sticky;
        top: ${headerHeight}px;
        margin: 0 -2rem;
        padding: 1rem 2rem;
        border-bottom: 1px solid ${
          searchBarIsStuck ? (blackBG ? 'rgba(255, 255, 255, 0.2)' : '#efeff0') : 'transparent'
        };

        @media screen and (max-width: ${breakpoints.small}px) {
          border-bottom: 1px solid ${blackBG ? 'rgba(255, 255, 255, 0.2)' : '#efeff0'};
          // on small screens the container is wrapped by a div with max-height: 0
          // to not push the body further below the invisible SearchBar
          position: absolute; 
          top: 0;
        }
      `)}

  ${isSlidingComponent &&
  `
    position: absolute; 
    top: 0;
    border-bottom: 1px solid ${blackBG ? 'rgba(255, 255, 255, 0.2)' : '#efeff0'};
    transform: translateY(${searchBarIsFocused ? '0' : `-${headerHeight + searchBarHeight}px`});
    opacity: ${searchBarIsFocused ? 1 : 0};
    transition:
      transform ${hideOnScrollAndScrollingDown ? '0.75s' : '0.3s'} ease-in-out,
      opacity ${hideOnScrollAndScrollingDown ? '0.75s' : '0.3s'} ease-in;
  `} 

  @media screen and (max-width: ${breakpoints.small}px) {
    margin: 0 -1.5rem;
    padding: 1rem 1.5rem;
    transform: translateY(${searchBarIsFocused ? '0' : `-${headerHeight + searchBarHeight}px`});
    opacity: ${searchBarIsFocused ? 1 : 0};
    transition:
      transform ${hideOnScrollAndScrollingDown ? '0.75s' : '0.3s'} ease-in-out,
      opacity ${hideOnScrollAndScrollingDown ? '0.75s' : '0.3s'} ease-in;
  }
`

const searchBarBlockStyle = (
  breakPoint: number,
  searchBarIsFocused: boolean,
  blackBG: boolean,
  isPartOfHeader?: boolean
) => css`
  box-sizing: border-box;
  max-width: 34rem;
  border: 1px solid ${searchBarIsFocused ? '#318eff' : '#efeff0'};
  border-radius: 0.5rem;
  padding: ${isPartOfHeader ? '7px 0.75rem' : '0.75rem 1rem'};

  ${blackBG &&
  `
    background-color: #39414E99;
    border-color: ${searchBarIsFocused ? '#fff' : '#808080'};
    color: #fafbfc;
  `}

  input {
    all: unset;
    text-align: left !important;
  }

  @media screen and (max-width: ${breakPoint}px) {
    margin: 0 auto;
    background-color: ${blackBG ? '#39414E99' : '#fff'};
  }
`

const searchDeleteImageStyle = (search: boolean) => css`
  ${search ? 'cursor: pointer' : 'display: none'};
`

// SEARCH
// RESULTS
// BLOCK
export const SearchResultsBlock: React.FunctionComponent<SearchResultsBlockProps> = ({
  blackBG = false,
  search,
  setSearch,
  debouncedSearch,
  resultsArray,
  resultsBlockContent,
  useContainerOnly = false,
  hideOnDesktop = false,
  searchBarIsPartOfHeader = true,
  withSearchBar = false,
  breakPoint = breakpoints.medium,
  cssStyles,
}) => {
  if (hideOnDesktop && isDesktop(breakPoint + 1)) {
    return null
  }

  return (
    <>
      {/* <ScrollToTop
        referenceIdForVisibility="search-results-reference"
        referenceIdForScrollingBlock="search-results-reference"
      /> */}
      <div
        id="search-results-block"
        css={[
          searchResultsContainerStyle(blackBG, searchBarIsPartOfHeader, withSearchBar, !!(search && debouncedSearch)),
          cssStyles,
        ]}
      >
        <div id="search-results-reference">
          {withSearchBar && setSearch && !isMobile(breakpoints.small) && (
            <SearchBar
              blackBG={blackBG}
              id="search-results-search-bar"
              isInSearchResultsBlock
              search={search}
              setSearch={setSearch}
              cssStyles={searchResultsSearchBarStyle(blackBG)}
            />
          )}
          {resultsArray.length > 0 ? (
            useContainerOnly ? (
              !(search && debouncedSearch) ? (
                <></>
              ) : (
                resultsBlockContent
              )
            ) : (
              // if search term is empty: show nothing
              <>
                {!(search && debouncedSearch) ? (
                  <></>
                ) : (
                  <Block
                    columns={3}
                    columnGap={1.5}
                    rowGapMobile={2}
                    twoColumnsForTablet
                    breakPoint={breakPoint}
                    css={searchResultsBlockStyle(withSearchBar)}
                    alignContent="stretch"
                  >
                    {resultsBlockContent}
                  </Block>
                )}
              </>
            )
          ) : (
            <NoSearchResultBlock
              useContainerOnly={useContainerOnly}
              withSearchBar={withSearchBar}
              debouncedSearch={debouncedSearch}
              breakPoint={breakPoint}
            />
          )}

          <CTAMain cssStyles={searchResultsCtaMainStyle} />
        </div>
      </div>
    </>
  )
}

const searchResultsContainerStyle = (
  blackBG: boolean,
  searchBarIsPartOfHeader: boolean,
  withSearchBar: boolean,
  searchTerm: boolean
) => css`
  position: fixed;
  background-color: ${blackBG ? 'rgb(7, 19, 34)' : '#fff'};
  z-index: 5;
  left: 0;
  top: 0;
  transform: translateY(${searchTerm ? '0' : '100%'});
  opacity: ${searchTerm ? 1 : 0};
  // padding-top to push the results below (mobile) header and search bar

  ${withSearchBar
    ? `
      padding: 0 var(--emptySpaceWidth) 0`
    : `
      padding: calc(4rem + ${
        searchBarIsPartOfHeader ? 0 : searchBarHeight
      }px + ${headerHeight}px) var(--emptySpaceWidth) 0;
  `};
  // reduce height to compensate for padding-top (line above)
  height: calc(100vh - 4rem - ${searchBarIsPartOfHeader ? 0 : searchBarHeight}px - ${headerHeight}px);
  ${withSearchBar && 'height: 100vh'};
  overflow-y: scroll;
  transition: all 0.3s ease-in-out;

  @media screen and (max-width: ${emptySpaceWidthBreakPoint}px) {
    padding-left: 2rem !important;
    padding-right: 2rem !important;
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    padding-left: 1.5rem !important;
    padding-right: 1.5rem !important;
  }

  @media screen and (max-width: ${headerBreakPoint}px) {
    padding: calc(4rem + ${searchBarIsPartOfHeader ? 0 : searchBarHeight}px + ${headerHeight}px) 1.5rem 0;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    padding: calc(2rem + ${searchBarHeight}px + ${headerHeight}px) 1.5rem 0;
    height: calc(100vh - 2rem - ${searchBarHeight}px - ${headerHeight}px);
  }
`

const searchResultsSearchBarStyle = (blackBG: boolean) => css`
  top: ${headerHeight - 1}px;
  border-bottom: 1px solid ${blackBG ? 'rgba(255, 255, 255, 0.2)' : '#efeff0'} !important;
`

const searchResultsCtaMainStyle = css`
  margin: 6rem 0;
`

const searchResultsBlockStyle = (withSearchBar: boolean) => css`
  ${withSearchBar && `margin-top: calc(3rem + ${searchBarHeight}px)`};
`

const noResultBlockStyle = (withSearchBar: boolean, breakPoint: number, useContainerOnly: boolean) => css`
  grid-column: 1 / -1;

  ${withSearchBar && `margin-top: calc(3rem + ${searchBarHeight}px)`};

  ${!withSearchBar && useContainerOnly && `margin-top: ${searchBarHeight}px`};

  .heading2.center {
    line-break: anywhere;
  }

  .subtitle {
    margin: 1rem !important;
  }

  @media screen and (max-width: ${breakPoint}px) {
    // 100vw - (padding left + right)
    width: calc(100vw - var(--emptySpaceWidth) * 2);
  }

  @media screen and (max-width: ${emptySpaceWidthBreakPoint}px) {
    // 100vw - (padding left + right)
    width: calc(100vw - 4rem);
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    // 100vw - (padding left + right)
    width: calc(100vw - 3rem);
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    margin-top: 0;

    .heading2.center {
      text-align: center !important;
    }
  }
`

// NO
// SEARCH
// RESULT
// BLOCK
export const NoSearchResultBlock: React.FunctionComponent<NoSearchResultBlockProps> = ({
  debouncedSearch,
  withSearchBar = false,
  useContainerOnly = false,
  breakPoint = headerBreakPoint,
  withCtaNewsletter = false,
}) => {
  return (
    <div css={noResultBlockStyle(withSearchBar, breakPoint, useContainerOnly)}>
      <div className="subtitle center no-margin">no results were found for</div>
      <h2 className="heading2 center no-margin">{debouncedSearch}</h2>
      <Image src="abstract/search-result.webp" alt="no search result" css={noResultImageStyle} />
      {withCtaNewsletter && <CTANewsletter />}
    </div>
  )
}

const noResultImageStyle = css`
  width: 20rem;
  margin: 2rem auto 8rem;
`
